import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Tally3,
  Info,
} from "lucide-react";

const SpiralMatrixVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "4x4 Square",
      matrix: [
        [1, 2, 3, 4],
        [5, 6, 7, 8],
        [9, 10, 11, 12],
        [13, 14, 15, 16],
      ],
    },
    {
      id: 2,
      name: "3x3 Square",
      matrix: [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
      ],
    },
    {
      id: 3,
      name: "3x5 Rectangle",
      matrix: [
        [1, 2, 3, 4, 5],
        [6, 7, 8, 9, 10],
        [11, 12, 13, 14, 15],
      ],
    },
    {
      id: 4,
      name: "5x3 Rectangle",
      matrix: [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
        [10, 11, 12],
        [13, 14, 15],
      ],
    },
    {
      id: 5,
      name: "Single Row",
      matrix: [[1, 2, 3, 4, 5]],
    },
    {
      id: 6,
      name: "Single Column",
      matrix: [[1], [2], [3], [4], [5]],
    },
    {
      id: 7,
      name: "Single Element",
      matrix: [[1]],
    },
    {
      id: 8,
      name: "Empty Matrix",
      matrix: [],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [matrix, setMatrix] = useState(defaultTestCases[0].matrix);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);
  const [spiralPath, setSpiralPath] = useState([]);

  // Pseudo code
  const pseudoCode = [
    { line: "function spiralOrder(matrix):", indent: 0 },
    { line: "if matrix is empty, return []", indent: 1 },
    { line: "", indent: 0 },
    { line: "R = matrix.length, C = matrix[0].length", indent: 1 },
    { line: "top = 0, bottom = R - 1", indent: 1 },
    { line: "left = 0, right = C - 1", indent: 1 },
    { line: "spiralPath = []", indent: 1 },
    { line: "", indent: 0 },
    { line: "while top <= bottom and left <= right:", indent: 1 },
    { line: "// Traverse Right (top row)", indent: 2 },
    {
      line: "for c = left to right: spiralPath.add(matrix[top][c])",
      indent: 3,
    },
    { line: "top++", indent: 3 },
    { line: "", indent: 0 },
    { line: "// Traverse Down (right column)", indent: 2 },
    {
      line: "for r = top to bottom: spiralPath.add(matrix[r][right])",
      indent: 3,
    },
    { line: "right--", indent: 3 },
    { line: "", indent: 0 },
    { line: "// Traverse Left (bottom row), if needed", indent: 2 },
    { line: "if top <= bottom:", indent: 3 },
    {
      line: "for c = right to left: spiralPath.add(matrix[bottom][c])",
      indent: 4,
    },
    { line: "bottom--", indent: 4 },
    { line: "", indent: 0 },
    { line: "// Traverse Up (left column), if needed", indent: 2 },
    { line: "if left <= right:", indent: 3 },
    {
      line: "for r = bottom to top: spiralPath.add(matrix[r][left])",
      indent: 4,
    },
    { line: "left++", indent: 4 },
    { line: "", indent: 0 },
    { line: "return spiralPath", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps for the spiral traversal
  const calculateSteps = (inputMatrix) => {
    const allSteps = [];
    let stepCount = 0;
    const R = inputMatrix.length;
    if (R === 0) {
      allSteps.push({
        step: 0,
        matrix: inputMatrix,
        description: "Matrix is empty. No traversal.",
        highlightLine: 1,
        spiralPath: [],
        boundaries: {},
        isFinal: true,
      });
      return allSteps;
    }
    const C = inputMatrix[0].length;
    let matrix = JSON.parse(JSON.stringify(inputMatrix));

    let top = 0,
      bottom = R - 1;
    let left = 0,
      right = C - 1;
    let path = [];

    const addStep = (
      description,
      highlightLine,
      currentCell = null,
      currentPath = [...path],
      boundaries = { top, bottom, left, right }
    ) => {
      allSteps.push({
        step: stepCount++,
        matrix: JSON.parse(JSON.stringify(matrix)),
        description,
        highlightLine,
        currentCell,
        spiralPath: currentPath,
        boundaries: JSON.parse(JSON.stringify(boundaries)), // Deep copy
      });
    };

    addStep("Initialize boundaries and an empty spiral path.", 6);

    while (top <= bottom && left <= right) {
      // Traverse Right
      addStep(
        `Traversing right from [${top}][${left}] to [${top}][${right}].`,
        10
      );
      for (let c = left; c <= right; c++) {
        path.push(matrix[top][c]);
        addStep(
          `Adding ${matrix[top][c]} from [${top}][${c}] to path.`,
          10,
          { r: top, c: c },
          path
        );
      }
      top++;
      addStep(`Finished right traversal. Increment top to ${top}.`, 11);

      // Traverse Down
      if (top <= bottom) {
        addStep(
          `Traversing down from [${top}][${right}] to [${bottom}][${right}].`,
          14
        );
        for (let r = top; r <= bottom; r++) {
          path.push(matrix[r][right]);
          addStep(
            `Adding ${matrix[r][right]} from [${r}][${right}] to path.`,
            14,
            { r: r, c: right },
            path
          );
        }
        right--;
        addStep(`Finished down traversal. Decrement right to ${right}.`, 15);
      }

      // Traverse Left
      if (top <= bottom && left <= right) {
        addStep(
          `Traversing left from [${bottom}][${right}] to [${bottom}][${left}].`,
          19
        );
        for (let c = right; c >= left; c--) {
          path.push(matrix[bottom][c]);
          addStep(
            `Adding ${matrix[bottom][c]} from [${bottom}][${c}] to path.`,
            19,
            { r: bottom, c: c },
            path
          );
        }
        bottom--;
        addStep(`Finished left traversal. Decrement bottom to ${bottom}.`, 20);
      }

      // Traverse Up
      if (top <= bottom && left <= right) {
        addStep(
          `Traversing up from [${bottom}][${left}] to [${top}][${left}].`,
          24
        );
        for (let r = bottom; r >= top; r--) {
          path.push(matrix[r][left]);
          addStep(
            `Adding ${matrix[r][left]} from [${r}][${left}] to path.`,
            24,
            { r: r, c: left },
            path
          );
        }
        left++;
        addStep(`Finished up traversal. Increment left to ${left}.`, 25);
      }
    }

    addStep("Algorithm complete. Final spiral path.", 28, null, path, {}, true);
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(matrix);
    setSteps(newSteps);
    setCurrentStep(0);
    setIsPlaying(false);
    setFinished(false);
    setSpiralPath([]);
  }, [matrix]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const stepData = steps[nextStep];
      setHighlightedLine(stepData.highlightLine);
      setSpiralPath(stepData.spiralPath);
      if (stepData.isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const stepData = steps[prevStep];
      setHighlightedLine(stepData.highlightLine);
      setSpiralPath(stepData.spiralPath);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
    setSpiralPath([]);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setMatrix(testCases[index].matrix);
  };

  const addCustomTestCase = () => {
    try {
      const parsedMatrix = JSON.parse(customInput);
      if (
        !Array.isArray(parsedMatrix) ||
        !parsedMatrix.every(
          (row) =>
            Array.isArray(row) && row.every((cell) => typeof cell === "number")
        )
      ) {
        throw new Error("Invalid format.");
      }
      if (
        parsedMatrix.length > 0 &&
        !parsedMatrix.every((row) => row.length === parsedMatrix[0].length)
      ) {
        throw new Error("All rows must have the same number of columns.");
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 7}`,
        matrix: parsedMatrix,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(
        "Invalid format. Use a JSON 2D array like [[1,2,3],[4,5,6]]. All rows must have the same length."
      );
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Tally3 className="text-blue-500" size={24} />
        Spiral Matrix Traversal
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an `m x n` matrix, return all elements of the matrix in
                spiral order.
              </p>
              <div className="mt-2 p-2 bg-white rounded">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Example:
                </p>
                <p className="text-xs font-mono text-gray-600">
                  Input: matrix = [[1,2,3],[4,5,6],[7,8,9]]
                  <br />
                  Output: [1,2,3,6,9,8,7,4,5]
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 font-mono">
                      {tc.matrix.length > 0
                        ? tc.matrix.map((row) => `[${row.join(",")}]`).join(" ")
                        : "[]"}
                    </div>
                  </button>
                  {tc.id > 8 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom matrix: [[1,2],[3,4]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>
            <div className="flex justify-center p-2 mb-4">
              {currentStepData &&
              currentStepData.matrix &&
              currentStepData.matrix.length > 0 ? (
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: `repeat(${
                      currentStepData.matrix[0]?.length || 1
                    }, minmax(0, 1fr))`,
                  }}
                >
                  {currentStepData.matrix.map((row, r) =>
                    row.map((cell, c) => {
                      const { boundaries, currentCell } = currentStepData;
                      let baseClass = "bg-gray-100 border-gray-300";

                      const inTopRow =
                        r === boundaries.top &&
                        c >= boundaries.left &&
                        c <= boundaries.right;
                      const inBottomRow =
                        r === boundaries.bottom &&
                        c >= boundaries.left &&
                        c <= boundaries.right;
                      const inLeftCol =
                        c === boundaries.left &&
                        r >= boundaries.top &&
                        r <= boundaries.bottom;
                      const inRightCol =
                        c === boundaries.right &&
                        r >= boundaries.top &&
                        r <= boundaries.bottom;

                      const isBoundary =
                        inTopRow || inBottomRow || inLeftCol || inRightCol;
                      const isCurrent =
                        currentCell?.r === r && currentCell?.c === c;

                      let cellValue = cell;

                      if (isCurrent) {
                        baseClass =
                          "bg-blue-400 border-blue-600 text-white scale-110";
                      } else if (isBoundary) {
                        baseClass = "bg-orange-100 border-orange-300";
                      }

                      const cellPathIndex = spiralPath.indexOf(cell);
                      const isVisited = currentStepData.spiralPath.some(
                        (val, idx) => {
                          // This check is tricky since values can repeat.
                          // For a robust check, we'd need to store (r,c) in the path.
                          // For this visualizer, let's simplify to check if the value is in the *current* spiralPath.
                          return (
                            val === cell &&
                            currentStepData.matrix[r][c] === cell
                          );
                        }
                      );

                      if (isVisited && !isCurrent) {
                        baseClass =
                          "bg-green-100 border-green-300 text-gray-600";
                        cellValue = (
                          <span className="text-green-600">{cell}</span>
                        );
                      }

                      return (
                        <div
                          key={`${r}-${c}`}
                          className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 font-mono text-sm font-bold ${baseClass}`}
                        >
                          {cellValue}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No matrix to display.</p>
              )}
            </div>
            <div className="mt-4 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold mb-2">Spiral Path:</h4>
              <div className="flex flex-wrap gap-1 text-sm font-mono max-h-24 overflow-y-auto">
                {spiralPath.length > 0 ? (
                  spiralPath.map((val, idx) => (
                    <span
                      key={idx}
                      className="bg-gray-200 px-2 py-1 rounded text-gray-800"
                    >
                      {val}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">Path is empty.</span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The algorithm processes the matrix in layers, moving inwards. It
              uses four pointers (top, bottom, left, right) to define the
              current boundaries of the spiral traversal. In each iteration, it
              traverses one side of the current "square" or "rectangle" and then
              shrinks the boundaries.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time Complexity:</span> O(R * C)
              </div>
              <div>
                <span className="font-medium">Space Complexity:</span> O(1)
                (excluding output array)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The four boundary pointers (`top`, `bottom`, `left`, `right`)
                  are crucial for controlling the traversal.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  After traversing each side, the corresponding boundary is
                  updated (e.g., `top++` after right traversal).
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Important checks (`if top &lt;= bottom` and `if left &lt;=
                  right`) are needed before traversing the bottom and left rows
                  to avoid duplicate elements in thin matrices.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 border-2 border-orange-300 rounded"></div>
                <span>Current layer boundaries</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-400 border-2 border-blue-600 text-white flex items-center justify-center rounded">
                  #
                </div>
                <span>Current cell being visited</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 border-2 border-green-300 text-green-600 flex items-center justify-center rounded">
                  #
                </div>
                <span>Visited cell (added to path)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiralMatrixVisualizer;
